(() => {
  if (customElements.get('bb-card-slider')) return;

  class BbCardSlider extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('data-bb-slider') || this._connected) return;
      this.track = this.querySelector('[data-bb-slider-track]');
      this.controls = this.querySelector('[data-bb-slider-controls]');
      this.pages = this.querySelector('[data-bb-slider-pages]');
      this.previous = this.querySelector('[data-bb-slider-prev]');
      this.next = this.querySelector('[data-bb-slider-next]');
      if (!this.track || !this.controls || !this.pages || !this.previous || !this.next) return;

      this._connected = true;
      this._abort = new AbortController();
      const signal = this._abort.signal;
      this.previous.addEventListener('click', () => this.goTo(this.page - 1), { signal });
      this.next.addEventListener('click', () => this.goTo(this.page + 1), { signal });
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this._scrollFrame);
        this._scrollFrame = requestAnimationFrame(() => this.updatePage());
      }, { passive: true, signal });
      this.track.addEventListener('pointerdown', (event) => this.startDrag(event), { signal });
      this.track.addEventListener('pointermove', (event) => this.moveDrag(event), { signal });
      this.track.addEventListener('pointerup', (event) => this.endDrag(event), { signal });
      this.track.addEventListener('pointercancel', (event) => this.endDrag(event), { signal });
      this.track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      this.track.addEventListener('click', (event) => {
        if (!this._suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        this._suppressClick = false;
      }, { capture: true, signal });
      this.track.addEventListener('wheel', (event) => this.onWheel(event), { passive: false, signal });
      this._resizeObserver = new ResizeObserver(() => this.refresh());
      this._resizeObserver.observe(this);
      window.addEventListener('resize', () => this.refresh(), { passive: true, signal });
      this.addEventListener('bb:slider:refresh', () => this.refresh(true), { signal });
      this.refresh();
    }

    disconnectedCallback() {
      this._resizeObserver?.disconnect();
      this._abort?.abort();
      cancelAnimationFrame(this._scrollFrame);
      clearTimeout(this._clickTimer);
      this._connected = false;
    }

    get slides() {
      return [...this.track.querySelectorAll('[data-bb-slider-slide]:not([hidden])')];
    }

    get visibleCount() {
      const key = innerWidth < 750 ? 'visibleMobile' : innerWidth < 990 ? 'visibleTablet' : 'visibleDesktop';
      return Math.max(1, parseInt(this.dataset[key], 10) || (key === 'visibleDesktop' ? 4 : key === 'visibleTablet' ? 2 : 1));
    }

    get loop() {
      return this.dataset.loop !== 'false';
    }

    startDrag(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      this._drag = { x: event.clientX, left: this.track.scrollLeft, page: this.page || 0, active: false };
    }

    moveDrag(event) {
      if (!this._drag) return;
      const distance = event.clientX - this._drag.x;
      if (!this._drag.active && Math.abs(distance) > 5) {
        this._drag.active = true;
        this.track.setPointerCapture(event.pointerId);
        this.track.style.scrollBehavior = 'auto';
        this.track.style.scrollSnapType = 'none';
        this.track.classList.add('is-dragging');
      }
      if (!this._drag.active) return;
      event.preventDefault();
      this.track.scrollLeft = this._drag.left - distance;
    }

    endDrag(event) {
      if (!this._drag) return;
      const drag = this._drag;
      this._drag = null;
      if (!drag.active) return;
      if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
      const moved = this.track.scrollLeft - drag.left;
      this.track.style.scrollBehavior = '';
      this.track.style.scrollSnapType = '';
      this.track.classList.remove('is-dragging');
      this._suppressClick = true;
      clearTimeout(this._clickTimer);
      this._clickTimer = setTimeout(() => { this._suppressClick = false; }, 250);
      const threshold = this.cardWidth * 0.25;
      this.goTo(drag.page + (moved > threshold ? 1 : moved < -threshold ? -1 : 0));
    }

    onWheel(event) {
      const delta = event.shiftKey ? event.deltaY : event.deltaX;
      if (!delta || (!event.shiftKey && Math.abs(delta) <= Math.abs(event.deltaY))) return;
      event.preventDefault();
      if (Date.now() - (this._lastWheel || 0) < 450) return;
      this._lastWheel = Date.now();
      this.goTo((this.page || 0) + Math.sign(delta));
    }

    refresh(reset = false) {
      const slides = this.slides;
      const visible = this.visibleCount;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const width = this.clientWidth;
      const edgePreview = this.dataset.edgePreview !== 'false';
      const bounds = this.getBoundingClientRect();
      const leftBleed = edgePreview ? Math.max(0, bounds.left) : 0;
      const rightBleed = edgePreview ? Math.max(0, innerWidth - bounds.right) : 0;
      // Reserve room for a partial next card only on one-card layouts. On wider
      // layouts the next card can peek into the space outside the page container.
      const previewWidth = edgePreview && slides.length > visible && visible === 1 ? Math.max(80, width * 0.2) : 0;
      this.cardWidth = Math.max(0, (width - gap * (visible - 1) - previewWidth) / visible);
      this.style.setProperty('--bb-slider-card-width', `${this.cardWidth}px`);
      this.style.setProperty('--bb-slider-left-bleed', `${leftBleed}px`);
      this.style.setProperty('--bb-slider-right-bleed', `${rightBleed}px`);
      this.style.setProperty('--bb-slider-edge-fade', `${Math.min(leftBleed, rightBleed, 24)}px`);
      const trailing = this.dataset.scrollToLast === 'true' ? width + rightBleed - this.cardWidth : rightBleed + previewWidth;
      this.style.setProperty('--bb-slider-trailing-space', `${Math.max(0, trailing)}px`);

      const maxIndex = this.dataset.scrollToLast === 'true' ? Math.max(0, slides.length - 1) : Math.max(0, slides.length - visible);
      const increment = Math.max(1, parseInt(this.dataset.slideIncrement, 10) || visible);
      this.positions = [0];
      for (let index = increment; index < maxIndex; index += increment) this.positions.push(index);
      if (maxIndex > this.positions.at(-1)) this.positions.push(maxIndex);
      this.pageCount = slides.length <= visible ? 1 : this.positions.length;
      this.controls.hidden = this.pageCount <= 1;
      if (this.pageCount <= 1) slides.forEach((slide) => slide.classList.remove('is-peek'));

      if (reset) this.track.scrollTo({ left: 0, behavior: 'instant' });
      this.pages.replaceChildren();
      if (this.pageCount > 8) {
        this.pages.setAttribute('aria-label', 'Huidige pagina');
      } else {
        this.pages.setAttribute('aria-label', 'Kies een pagina');
        for (let index = 0; index < this.pageCount; index += 1) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'bb-card-slider__page';
          button.setAttribute('aria-label', `Ga naar pagina ${index + 1} van ${this.pageCount}`);
          button.addEventListener('click', () => this.goTo(index));
          this.pages.append(button);
        }
      }
      this.updatePage();
    }

    goTo(index) {
      if (this.pageCount <= 1) return;
      let page = index;
      if (this.loop) page = (page + this.pageCount) % this.pageCount;
      else page = Math.max(0, Math.min(page, this.pageCount - 1));
      const slides = this.slides;
      const target = slides[this.positions[page]];
      if (!target) return;
      this.page = page;
      this.updateControls();
      this.track.scrollTo({
        left: target.offsetLeft - slides[0].offsetLeft,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      });
    }

    updatePage() {
      const slides = this.slides;
      if (!slides.length || !this.positions) return;
      const step = this.cardWidth + (parseFloat(getComputedStyle(this.track).columnGap) || 0);
      const index = step ? this.track.scrollLeft / step : 0;
      let closest = 0;
      this.positions.forEach((position, page) => {
        if (Math.abs(position - index) < Math.abs(this.positions[closest] - index)) closest = page;
      });
      this.page = closest;
      this.updateControls();
    }

    updateControls() {
      if (this.pageCount <= 1) return;
      const page = this.page || 0;
      this.previous.disabled = !this.loop && page === 0;
      this.next.disabled = !this.loop && page === this.pageCount - 1;
      if (this.pageCount > 8) {
        this.pages.textContent = `${page + 1} / ${this.pageCount}`;
      } else {
        [...this.pages.children].forEach((button, index) => {
          button.setAttribute('aria-current', index === page ? 'true' : 'false');
        });
      }
      const start = this.positions[page];
      this.slides.forEach((slide, index) => {
        slide.classList.toggle('is-peek', index < start || index >= start + this.visibleCount);
      });
    }
  }

  customElements.define('bb-card-slider', BbCardSlider);
})();
